import { memo, useEffect, useRef, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { Modal } from '../../../../adminPages/components/Modal/Modal'
import { AnimatedSpinner } from '../../../../components/AnimatedSpinner'
import { useMicrophones } from '../../../../hooks/useMicrophones'
import { useSessionWebRTC } from '../../../../hooks/useSessionWebRTC'
import { Controls } from '../../../../pages/SessionPage/Controls/Controls'
import type { WebRTCMessage } from '../../../../types/webRtcMessage'
import { Heading } from '../Heading'

interface AvatarDisplayProps {
  avatarId: string
  mentorId: string
  slug: string
  isDeleting: boolean
  isPublishing?: boolean
  onPublish: () => void
  onRestart: () => void
}

const AvatarDisplay: React.FC<AvatarDisplayProps> = memo(
  ({
    avatarId,
    mentorId,
    slug,
    isPublishing = false,
    isDeleting,
    onPublish,
    onRestart,
  }) => {
    const { isAuthenticated } = useAuth()
    const videoRef = useRef<HTMLVideoElement>(null)
    const audioRef = useRef<HTMLAudioElement>(null)
    const [avatarReady, setAvatarReady] = useState(false)
    const [isAvatarTalking, setIsAvatarTalking] = useState(false)
    const [showRestartModal, setShowRestartModal] = useState(false)
    const {
      isMuted,
      muteMicrophone,
      stream: userMicrophoneStream,
      selectedMicrophone,
      connectMicrophone,
    } = useMicrophones(false)

    const handleWebRTCMessage = (data: WebRTCMessage) => {
      console.log('AvatarDisplay WebRTC Message:', data)
      switch (data.event) {
        case 'avatar_state_changed':
          console.log('Avatar state changed:', data.data?.state)
          if (data.data?.state === 'ready') {
            setAvatarReady(true)
          }
          break
        case 'message_received': {
          const { message } = data.data || {}
          if (
            message?.action === 'completed' &&
            typeof message.content === 'string' &&
            message.role
          ) {
            console.log('Message received:', message.content)
          }
          break
        }
        case 'talk_state_changed':
          setIsAvatarTalking(data.data?.state === 'started')
          console.log('Avatar talking state changed:', data.data?.state)
          break
      }
    }

    const handleRestartClick = () => {
      setShowRestartModal(true)
    }

    const handleCloseModal = () => {
      if (!isDeleting) {
        setShowRestartModal(false)
      }
    }

    useEffect(
      () => () => {
        setShowRestartModal(false)
      },
      [],
    )

    const { videoStream, audioStream } = useSessionWebRTC({
      avatarId,
      mentorId,
      userMicrophoneStream,
      selectedMicrophone,
      prevMentorId: null,
      context: '',
      companionSlug: slug,
      isAuthenticated,
      isInterview: false,
      onMessage: handleWebRTCMessage,
    })

    // Connect microphone on mount
    useEffect(() => {
      connectMicrophone().catch(error => {
        console.error('Error connecting microphone:', error)
      })
    }, [])

    // Set video stream
    useEffect(() => {
      if (videoRef.current) {
        videoRef.current.srcObject = videoStream || null
      }
    }, [videoStream])

    // Set audio stream
    useEffect(() => {
      if (audioRef.current) {
        audioRef.current.srcObject = audioStream || null
      }
    }, [audioStream])

    return (
      <div className="w-[750px] max-w-full mx-auto mb-auto sm:mb-0">
        <Heading
          title="Review Your Twin"
          subtitle="Talk with your digital twin and try it out before publishing it."
        />
        <div className="relative w-full object-cover aspect-video rounded-2xl tc-mv-blur overflow-hidden">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            disablePictureInPicture
            data-testid="avatar-main-video"
            className="w-full h-full object-cover aspect-video rounded-2xl"
          />
          {audioStream && <audio ref={audioRef} autoPlay />}

          {avatarReady && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-4">
              <Controls
                isTalking={isAvatarTalking}
                hasNextSpeech={null}
                isMuted={isMuted}
                personaData={null}
                userMicrophoneStream={userMicrophoneStream}
                selectedMicrophone={selectedMicrophone}
                contextParam={null}
                audioRef={audioRef}
                isAuthenticated={false}
                setMuted={muteMicrophone}
                setOpenAiVoice={() => {}}
                handleRepeatCurrentSpeech={() => {}}
                disableStopButton={true}
                disableEndButton={true}
                controls={true}
              />
            </div>
          )}
          {!avatarReady && (
            <div className="start-experience-btn loader !absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <span className="button-content relative">
                <span className="star-icon star-first">✦</span>
                <span className="star-icon star-second large">✦</span>
                <span className="star-icon star-third">✦</span>
                <span className="text ms-2">Connecting...</span>
              </span>
            </div>
          )}
        </div>
        <div className="flex gap-4 justify-center my-8">
          <button
            data-testid="avatar-restart-btn"
            className="btn btn-secondary"
            onClick={handleRestartClick}
            disabled={!avatarReady || isDeleting}
          >
            <i className="fa-solid fa-rotate-right mr-2" /> Restart
          </button>
          <button
            data-testid="avatar-publish-btn"
            className="btn btn-primary"
            onClick={onPublish}
            disabled={isPublishing}
          >
            {isPublishing ? 'Publishing...' : 'Publish'}{' '}
            <i className="fa-solid fa-arrow-right ml-2" />
          </button>
        </div>

        {showRestartModal && (
          <Modal
            title="Restart Digital Twin Creation"
            onClose={handleCloseModal}
          >
            <>
              <p className="text-white py-4 px-6">
                Are you sure you want to restart the creation process? This will
                delete your current Digital Twin and you'll need to start over
                from the beginning.
              </p>
              <div className="flex justify-end gap-4 border-t border-white/10 py-4 px-6">
                <button
                  data-testid="avatar-restart-modal-cancel-btn"
                  onClick={handleCloseModal}
                  disabled={isDeleting}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  data-testid="avatar-restart-modal-confirm-btn"
                  onClick={onRestart}
                  disabled={isDeleting}
                  className="btn bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  {isDeleting && <AnimatedSpinner className="mr-2" />}
                  Restart
                </button>
              </div>
            </>
          </Modal>
        )}
      </div>
    )
  },
)

export { AvatarDisplay }
